import React, { useEffect, useRef, useState } from 'react';
import { ConfigProvider, Table } from 'tea-component';
import { SortBy } from 'tea-component/lib/table/addons/sortable';
import { getHeroTableDate } from '@/pages/opinion/services/hero';
import { dealwithTableData } from '../../utils';
import PercentageItem from '@/pages/opinion/components/Snippets/PercentageItem';
import FilterBoxCommon from '@/pages/opinion/components/FilterBoxCommon';
import UserInfoItem from '@/pages/opinion/components/Snippets/UserInfoItem';
import Sentiment from '@/pages/opinion/components/Snippets/Sentiment';
import TableExpandableDetail from '@/pages/opinion/components/TableExpandableDetail';
import { getLocale, useIntl } from 'umi';
import { getTeaLocale } from '@/pages/home/utils/lang';
import CardCommon from '@/pages/opinion/components/CardCommon';
import useKeywordColumn from '@/pages/opinion/hooks/useKeywordColumn';
import { percentageFormat, reviewsFormat } from '@/pages/opinion/utils/value';
import './index.scss';
import { LanguageType } from '@/pages/opinion/types/common';
import { getFeedsStats } from '@/pages/opinion/services/feeds';
import { formatDateFunc } from '@op/utils/time';
import { useDrilldownParams, EDrilldownMode } from '@op/hooks/useDrilldownParams';
import { useDownloadParams } from '@op/hooks/useDownloadParams';

const { expandable, pageable, sortable, autotip, scrollable } = Table.addons;

const columnsWidth = 240;
export default function HeroTable({
  commonParams,
  languageType,
  setLanguageType,
}: {
  commonParams: any;
  languageType: LanguageType;
  setLanguageType: (value: LanguageType) => void;
}) {
  const { formatMessage } = useIntl();
  const [expandedKeys, setExpandedKeys] = useState<Array<string>>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(false);
  const [sort, setSort] = useState<SortBy<any>>({
    by: 'mentions',
    order: 'desc',
  });
  const [tableRecords, setTableRecord] = useState<Array<any>>([]);
  const [pageInfo, setPageInfo] = useState({ pageSize: 10, pageIndex: 1 });
  const formatDate = formatDateFunc(commonParams.date_type);
  const getTableData = (newPageInfo?: {
    pageIndex: number;
    pageSize: number;
  }) => {
    if (Object.keys(commonParams).length === 0 || loading) return;
    setExpandedKeys([]);
    const { pageSize, pageIndex } = newPageInfo || pageInfo;
    setLoading(true);
    setApiError(false);
    const { by, order } = sort;
    setTableRecord([]);
    getHeroTableDate({
      ...commonParams,
      sort_model: order,
      sort_item: by,
      page: pageIndex,
      page_size: pageSize,
    })
      .then((data: any) => {
        const { new_list, total, old_list } = data || {};
        setRecord(new_list);
        setTableRecord(dealwithTableData(new_list, old_list));
        setTotal(total || 0);
      })
      .finally(() => {
        setLoading(false);
        init.current = false;
      });
  };

  /* 处理keyword并发，导致没有数据的问题 */
  const { setRecord, KeywordColumn } = useKeywordColumn({
    commonParams: commonParams,
    dealwithParams: (item, otherParams) => {
      return {
        ...otherParams,
        keyword_top_n: 9,
        keyword_sample_size: '50',
        keyword_widely_match: true,
        keyword_en: [item.name],
        page_source: 'hero',
      };
    },
    dealwithKey: (param: any) => param?.keyword_en?.[0],
  });

  const init = useRef(true);

  useEffect(() => {
    if (!init.current) getTableData();
  }, [pageInfo, sort]);

  useEffect(() => {
    // 这里用浅拷贝，因为我不想因为我设置了pageInfo导致触发上面那个useEffect
    let newPageInfo = Object.assign(pageInfo, { pageIndex: 1 });
    setPageInfo(newPageInfo);
    getTableData(newPageInfo);
  }, [commonParams]);

  const getPercentageElement = (
    percentage: number,
    otherElement: JSX.Element | string | number,
  ) => {
    return (
      <div className="item-info">
        <PercentageItem percentage={percentage}>
          {(element) => (
            <>
              {otherElement}
              {element}
            </>
          )}
        </PercentageItem>
      </div>
    );
  };

  const { goPage } = useDrilldownParams();
  const { downloadLegacy, downloading } = useDownloadParams();

  // 点击话题跳转Feeds下有数据的tab
  const clickTopicToFeeds = async (key: string) => {
    const tab = await getFeedsStats({
      ...commonParams,
      keyword_en: [key],
      keyword_widely_match: true,
      match_model: 'exact',
      review_type: 'posts',
    }).then((data: any) => {
      let tab = 'socialMedia';
      let { social_total, comments_total } = data || {};
      if (social_total === 0 && comments_total > 0) {
        tab = 'gameStore';
      }
      return tab;
    });
    goPage({
      path: `/opinion/Feeds/Feeds?tab=${tab}`,
      filter: {
        ...commonParams,
        statsDetailType: 'feeds-hero',
        keyword_en: [key],
        page_source: 'hero',
      },
      mode: EDrilldownMode.Local,
    });
  };
  const locale = getTeaLocale(getLocale());
  return (
    <div className="dbo-hero-tablecard">
      <FilterBoxCommon
        left={[{ key: 'language', defaultValue: 'origin' }]}
        right={[
          {
            key: 'download',
            state: downloading ? 'loading' : '',
            event: {
              onClick: () => {
                downloadLegacy({
                  limit: 150000,
                  params: {
                    ...commonParams,
                    sort_model: sort.order,
                    sort_item: sort.by,
                    page: 1,
                    page_size: 1000,
                  },
                  downloadFunc: 'getHeroTableDate',
                  sheetName: 'Hero',
                  total,
                  simpleName: 'Hero',
                });
              },
            },
          },
        ]}
        onChange={(value, key) => {
          if (key === 'language') {
            setLanguageType(value[key]);
          }
        }}
      ></FilterBoxCommon>
      <CardCommon>
        <ConfigProvider locale={locale}>
          <Table
            records={tableRecords}
            className="dbo-herotable"
            recordKey="name_en"
            columns={[
              {
                key: 'hero',
                header: formatMessage({ id: 'Hero' }),
                width: 220,
                fixed: 'left',
                render: ({ icon, name_zh, name_en }) => (
                  <UserInfoItem
                    isLink
                    iconClick={() => clickTopicToFeeds(name_en)}
                    onClick={() => clickTopicToFeeds(name_en)}
                    children={(className) => (
                      <span className={`${className} herotable-subname`}>
                        {name_en}
                      </span>
                    )}
                    icon={icon}
                    name={name_zh}
                  />
                ),
              },
              {
                key: 'mentions',
                header: formatMessage({ id: 'Mentions' }),
                width: 200,
                align: 'right',
                render: ({ mentions, mentions_than }) =>
                  getPercentageElement(mentions_than, reviewsFormat(mentions)),
              },
              {
                key: 'non_negative_ratio',
                header: formatMessage({ id: 'Non Negative%' }),
                align: 'right',
                width: columnsWidth,
                render: ({ non_negative_ratio, non_negative_ratio_than }) =>
                  getPercentageElement(
                    non_negative_ratio_than,
                    percentageFormat(non_negative_ratio * 100),
                  ),
              },
              {
                key: 'sentiment',
                header: formatMessage({ id: 'Sentiment' }),
                align: 'right',
                width: columnsWidth,
                render: ({ sentiment, sentiment_than }) => {
                  const element = (
                    <Sentiment
                      className="icon-info"
                      sentiment={sentiment}
                    ></Sentiment>
                  );
                  return getPercentageElement(sentiment_than, element);
                },
              },
              {
                key: 'negative_mentions',
                header: formatMessage({ id: 'Negative' }),
                align: 'right',
                width: columnsWidth,
                render: ({ negative_mentions, negative_mentions_than }) =>
                  getPercentageElement(
                    negative_mentions_than,
                    reviewsFormat(negative_mentions),
                  ),
              },
              {
                key: 'keywords',
                header: formatMessage({ id: 'Keywords' }),
                render: ({ name }: any) => {
                  return (
                    <KeywordColumn
                      dataKey={name}
                      filterKeyword={(records: Array<any>) => {
                        return records?.filter(
                          (item) => item.keyword?.toLocaleLowerCase() !== name,
                        );
                      }}
                      itemClick={(children) => {
                        goPage({
                          path: '/opinion/Stats/Detail',
                          filter: {
                            filter: {
                              ...commonParams,
                              keyword: children,
                              keyword_en: [name],
                              keyword_widely_match: true,
                              page_source: 'hero',
                            },
                            statsDetailType: 'hero-keyword',
                          },
                        });
                      }}
                      language={languageType}
                    ></KeywordColumn>
                  );
                },
              },
            ]}
            addons={[
              expandable({
                expandedKeys,
                rowExpand: true,
                onExpandedKeysChange: (keys, { event }) => {
                  event.stopPropagation();
                  setExpandedKeys(keys);
                },
                render(item) {
                  const {
                    name,
                    global_gpt_summary,
                    sea_gpt_summary,
                    gpt_summary_start_time,
                    gpt_summary_end_time,
                  } = item;
                  const haveTime =
                    gpt_summary_start_time && gpt_summary_end_time;
                  return (
                    <TableExpandableDetail
                      language={languageType}
                      params={{
                        ...commonParams,
                        keyword_en: [name],
                      }}
                      hasGptPanel
                      gptValue={[
                        {
                          title: 'Summary Data Range',
                          value: haveTime
                            ? `${formatDate(
                                gpt_summary_start_time,
                              )} ~ ${formatDate(gpt_summary_end_time)}`
                            : '',
                        },
                        {
                          title: 'Global Hero Summary',
                          value: global_gpt_summary,
                        },
                        {
                          title: 'SEA Hero Summary',
                          value: sea_gpt_summary,
                        },
                      ]}
                    ></TableExpandableDetail>
                  );
                },
              }),
              sortable({
                // 这两列支持排序，其中 age 列优先倒序，mail 采用自定义排序方法
                columns: [
                  'mentions',
                  'non_negative_ratio',
                  'negative_mentions',
                  'sentiment',
                ],
                value: [sort],
                onChange: (_value, { sort }) => {
                  setSort(sort);
                },
              }),
              pageable({
                recordCount: total,
                pageIndex: pageInfo.pageIndex,
                pageSize: pageInfo.pageSize,
                onPagingChange: ({ pageSize, pageIndex }) => {
                  setPageInfo({
                    pageIndex: pageIndex || 1,
                    pageSize: pageSize || 10,
                  });
                },
              }),
              autotip({
                isLoading: loading,
                loadingText: '',
              }),
              scrollable({
                minWidth: 2130,
              }),
            ]}
          />
        </ConfigProvider>
      </CardCommon>
    </div>
  );
}
